import React, { useState } from 'react'
import { DATA_TEST_CLASS } from 'shared-constants/build/testIds'
import type { GridTemplateColumnsProps } from 'styled-system'
import { Box, Grid, Flex } from 'components/Layout'
import Link, { LinkWithText } from 'components/Link'
import { Text } from 'components/Typography'
import { SESSION_TYPE } from 'constants/common'
import { ROUTE } from 'constants/routes'
import { SPACE } from 'Theme'
import { DetailLabel } from './DetailLabel'
import { SessionPreviewWrapper, Row } from './SessionPreviewWrapper'

interface SessionPreviewProps extends GridTemplateColumnsProps {
  sessionType?: (typeof SESSION_TYPE)[keyof typeof SESSION_TYPE]
  participants?: {
    firstName: string
    lastName: string
    email: string
  }[]
  message?: string
  password?: string
  reviewToken?: string
  hasDetail?: boolean
  isDetailOpened?: boolean
  isZoomLinkVisible?: boolean
  footerComponent?: () => React.ReactNode
  zoomSession?: {
    id?: string
    url?: string
  }
  guests?: string[]
}

export const SessionPreview: React.FC<React.PWC<SessionPreviewProps>> = ({
  children,
  hasDetail = true,
  isDetailOpened = false,
  participants,
  isZoomLinkVisible = true,
  zoomSession,
  password,
  reviewToken,
  footerComponent: FooterComponent,
  sessionType,
  guests,
  message,
  gridTemplateColumns = {
    MOBILE: '1fr',
    TABLET: '20% 20% auto 20%',
  },
}) => {
  const [isDetailVisible, setIsDetailVisible] = useState(isDetailOpened)
  const fullNames =
    participants?.map(
      ({ firstName, lastName }) => `${firstName} ${lastName}`
    ) || []
  const emails = participants?.map(({ email }) => email) || []
  return (
    <Box data-test-class={DATA_TEST_CLASS.SESSION_PREVIEW_WRAPPER}>
      <SessionPreviewWrapper
        hasDetail={hasDetail}
        isDetailVisible={isDetailVisible}
        setIsDetailVisible={setIsDetailVisible}
        gridTemplateColumns={gridTemplateColumns}
      >
        {children}
      </SessionPreviewWrapper>
      {hasDetail && isDetailVisible && (
        <Box mt={SPACE.PX_20}>
          <Row
            gridTemplateColumns={{
              MOBILE: '1fr',
              TABLET: '20% 20% auto',
            }}
            mb={SPACE.PX_15}
            gridGap={{
              MOBILE: 0,
              TABLET: SPACE.PX_15,
            }}
          >
            <Grid
              gridColumn={{
                MOBILE: 1,
                TABLET: 2,
              }}
              mb={SPACE.PX_15}
            >
              <Flex flexDirection="column">
                {isZoomLinkVisible && (
                  <Flex flexDirection="column">
                    <DetailLabel bold marginBottom>
                      Join zoom meeting
                    </DetailLabel>
                    <Text>
                      {zoomSession?.url ? (
                        <Link
                          href={zoomSession.url}
                          target="_blank"
                          aria-label="zoom meeting"
                        >
                          meeting {zoomSession.id}
                        </Link>
                      ) : (
                        'Meeting will be available 24 hours before consultation'
                      )}
                    </Text>
                  </Flex>
                )}
                {password && (
                  <Flex flexDirection="column" mt={SPACE.PX_15}>
                    <DetailLabel bold marginBottom>
                      Password
                    </DetailLabel>
                    <Text mb={SPACE.PX_15}>{password}</Text>
                  </Flex>
                )}
                {reviewToken && (
                  <Flex flexDirection="column" mt={SPACE.PX_15}>
                    <DetailLabel bold marginBottom>
                      Review link
                    </DetailLabel>
                    <LinkWithText
                      href={ROUTE.CLIENT_SESSION_REVIEW({ reviewToken })}
                      data-test-class={DATA_TEST_CLASS.SESSION_REVIEW_TOKEN_BTN}
                    >
                      Leave a review
                    </LinkWithText>
                  </Flex>
                )}
              </Flex>
            </Grid>
            <Flex>
              <Grid
                ml={{ MOBILE: 0, TABLET: SPACE.PX_40 }}
                gridColumn={{
                  MOBILE: 1,
                  TABLET: 2,
                }}
                gap={SPACE.PX_15}
              >
                {fullNames.length > 0 && (
                  <Flex flexDirection="column">
                    <DetailLabel bold marginBottom>
                      {`Participant${fullNames.length > 1 ? 's' : ''}`}
                    </DetailLabel>
                    <Text>{fullNames.join(', ')}</Text>
                  </Flex>
                )}
                {emails.length > 0 && (
                  <Flex flexDirection="column">
                    <DetailLabel bold marginBottom>
                      {sessionType === SESSION_TYPE.GROUP
                        ? 'user emails'
                        : 'user email'}
                    </DetailLabel>
                    <Text>{emails.join(', ')}</Text>
                  </Flex>
                )}
                {guests && guests.length > 0 && (
                  <Flex flexDirection="column">
                    <DetailLabel bold marginBottom>
                      Guests
                    </DetailLabel>
                    <Text>{guests.join(', ')}</Text>
                  </Flex>
                )}
                {message && (
                  <Flex flexDirection="column">
                    <DetailLabel bold marginBottom>
                      Message
                    </DetailLabel>
                    <Text
                      data-test-class={DATA_TEST_CLASS.CLIENT_SESSION_MESSAGE}
                    >
                      {message}
                    </Text>
                  </Flex>
                )}
              </Grid>
            </Flex>
          </Row>
          {FooterComponent?.()}
        </Box>
      )}
    </Box>
  )
}
